import { Button } from '@foam/buoy/src/components/Button/Button';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import type { Creator } from '~/clientApi/creator.model';
import { useSuperCreatorEditStore } from '~/components/SuperCreatorEditModal';
import { routes } from '~/core/router';
import type { ICreator } from '~/types';

import type { EmptyStateButtonType } from './utils';

interface AllEmptyStateProps {
  text: string;
  creator: Creator;
  buttonType?: EmptyStateButtonType;
}

export const AllEmptyState = ({
  text,
  creator,
  buttonType,
}: AllEmptyStateProps) => {
  const router = useRouter();
  const { openSuperEditModal } = useSuperCreatorEditStore();

  const handleConnect = useCallback(async () => {
    if (creator.slug) {
      await router.push(
        {
          pathname: routes.talentProfilePlatformConnections(creator.slug),
          query: { creatorSlug: creator.slug },
        },
        undefined,
        { shallow: true },
      );
    }
  }, [creator.slug, router]);

  const handleAddLocation = useCallback(() => {
    openSuperEditModal(creator as ICreator);
  }, [creator, openSuperEditModal]);

  const ctaButton = useMemo(() => {
    if (!buttonType) {
      return null;
    }

    const buttonVariants = {
      connect: {
        type: 'connect',
        text: 'Connect',
        onClick: handleConnect,
      },
      addLocation: {
        type: 'addLocation',
        text: 'Add Location',
        onClick: handleAddLocation,
      },
    };

    return {
      buttonType: buttonVariants[buttonType].type,
      button: (
        <Button
          size="lg"
          className="w-fit"
          onClick={buttonVariants[buttonType].onClick}
        >
          {buttonVariants[buttonType].text}
        </Button>
      ),
    };
  }, [buttonType]);

  return (
    <>
      <div className="tablet:flex-col flex size-full flex-col items-start justify-between overflow-hidden rounded-16 border border-neutral-100 bg-neutral-25 p-8 mac:flex-row">
        <div className="flex flex-col gap-4 md:w-full">
          <p className="w-full pr-10 text-20 font-500 text-neutral-400 mac:max-w-[550px]">
            {text}
          </p>
          {ctaButton?.button}
        </div>

        <Image
          src="/charts/all_chart_banner.png"
          className="hidden max-w-[700px] md:mt-4 md:block"
          alt={text}
          width={733}
          height={100}
        />
      </div>
    </>
  );
};
